import React from "react";
import {
  Box,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import MimeIcon from "./MimeIcon";
import { humanReadableSize } from "./app/utils";
import { ViewMode } from "./App";

export interface FileItem {
  key: string;
  size: number;
  uploaded: string;
  httpMetadata: { contentType: string };
  customMetadata?: { thumbnail?: string };
}

function extractFilename(key: string) {
  return key.split("/").pop();
}

export function encodeKey(key: string) {
  return key.split("/").map(encodeURIComponent).join("/");
}

export function isDirectory(file: FileItem) {
  return file.httpMetadata?.contentType === "application/x-directory";
}

function FileGrid({
  files,
  onCwdChange,
  onFileClick,
  multiSelected,
  onMultiSelect,
  emptyMessage,
  viewMode = "grid",
}: {
  files: FileItem[];
  onCwdChange: (newCwd: string) => void;
  onFileClick: (file: FileItem) => void;
  multiSelected: string[] | null;
  onMultiSelect: (key: string) => void;
  emptyMessage?: React.ReactNode;
  viewMode?: ViewMode;
}) {
  if (files.length === 0) {
    return <>{emptyMessage}</>;
  }

  const renderFileItem = (file: FileItem) => (
    <ListItemButton
      key={file.key}
      selected={multiSelected?.includes(file.key)}
      onClick={() => {
        if (multiSelected !== null) {
          onMultiSelect(file.key);
        } else if (isDirectory(file)) {
          onCwdChange(file.key + "/");
        } else {
          onFileClick(file);
        }
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        onMultiSelect(file.key);
      }}
      sx={{ userSelect: "none" }}
    >
      <ListItemIcon>
        {file.customMetadata?.thumbnail ? (
          <img
            src={`/webdav/_$flaredrive$/thumbnails/${file.customMetadata.thumbnail}.png`}
            alt={file.key}
            style={{ width: 36, height: 36, objectFit: "cover" }}
          />
        ) : (
          <MimeIcon contentType={file.httpMetadata.contentType} />
        )}
      </ListItemIcon>
      <ListItemText
        primary={extractFilename(file.key)}
        primaryTypographyProps={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        secondary={
          <React.Fragment>
            <Box
              sx={{
                display: "inline-block",
                minWidth: "160px",
                marginRight: 1,
              }}
            >
              {new Date(file.uploaded).toLocaleString()}
            </Box>
            {!isDirectory(file) && humanReadableSize(file.size)}
          </React.Fragment>
        }
      />
    </ListItemButton>
  );

  if (viewMode === "list") {
    return (
      <List sx={{ paddingBottom: "48px" }}>
        {files.map((file) => (
          <ListItemButton
            key={file.key}
            selected={multiSelected?.includes(file.key)}
            onClick={() => {
              if (multiSelected !== null) {
                onMultiSelect(file.key);
              } else if (isDirectory(file)) {
                onCwdChange(file.key + "/");
              } else {
                onFileClick(file);
              }
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              onMultiSelect(file.key);
            }}
            sx={{ userSelect: "none", display: "flex", alignItems: "center" }}
          >
            <ListItemIcon sx={{ minWidth: 48 }}>
              {file.customMetadata?.thumbnail ? (
                <img
                  src={`/webdav/_$flaredrive$/thumbnails/${file.customMetadata.thumbnail}.png`}
                  alt={file.key}
                  style={{ width: 36, height: 36, objectFit: "cover" }}
                />
              ) : (
                <MimeIcon contentType={file.httpMetadata.contentType} />
              )}
            </ListItemIcon>
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <Typography
                noWrap
                sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {extractFilename(file.key)}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: 160, flexShrink: 0, textAlign: "right", mx: 2 }}
            >
              {new Date(file.uploaded).toLocaleString()}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: 80, flexShrink: 0, textAlign: "right" }}
            >
              {!isDirectory(file) ? humanReadableSize(file.size) : "—"}
            </Typography>
          </ListItemButton>
        ))}
      </List>
    );
  }

  return (
    <Grid container sx={{ paddingBottom: "48px" }}>
      {files.map((file) => (
        <Grid item key={file.key} xs={12} sm={6} md={4} lg={3} xl={2}>
          {renderFileItem(file)}
        </Grid>
      ))}
    </Grid>
  );
}

export default FileGrid;
